import {useCallback, useEffect, useMemo} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {MousePointer2, X} from 'lucide-react';
import type {AiChatInputPlugin} from '@tinct/web-ai';
import {AiChat, createContextSummaryPlugin, createInputPlugin} from '@tinct/web-ai';
import {ContextContentBlock, EmbeddedBlock, EmbeddedPage} from '@tinct/core-api-models';
import {invalidatePageQueries} from '@tinct/core-api';
import {useGetCurrentQuotas, useGetCurrentWorkspaceId} from '@tinct/core-workspace';
import {useOpenPaywall} from '@tinct/web-feature-payment';
import {usePageChat, usePublishedEditNotice} from '../hooks';
import {PreviewChatEmpty} from './preview-chat-empty';
import {ChangesNotLiveModal} from '../../../modals/changes-not-live-modal/changes-not-live-modal';

type StudioChatPanelProps = {
  campaignId: string;
  /** The selected variant page — null while the original page is selected. */
  page: EmbeddedPage | null;
  /**
   * The selected block — optional chat context, shown as a chip in the
   * composer (same as the edit-landing-page chat). The conversation itself
   * is per page; the selection only scopes the next ask, and needs no
   * variant to exist on the page.
   */
  block: EmbeddedBlock | null;
  onDeselectBlock?: () => void;
  /** Locks the composer, e.g. while the campaign's variants are generating. */
  disabled?: boolean;
};

function PageChat({
  campaignId,
  page,
  block: selectedBlock,
  onDeselectBlock,
  disabled,
}: StudioChatPanelProps & { page: EmbeddedPage }) {
  const pageId = page.id as string;

  const {
    messages,
    eventBus,
    isLoadingMore,
    hasMore,
    handleSendMessage,
    handleLoadMore,
  } = usePageChat({
    pageId,
    // The selection didn't resolve server-side (stale block) — the chip must
    // not keep promising a scope the agent isn't using.
    onContextIgnored: onDeselectBlock,
  });

  const { lpGenerationQuota } = useGetCurrentQuotas();
  const { openPaywall } = useOpenPaywall();
  const workspaceId = useGetCurrentWorkspaceId();

  // The raw message plus the selected block's id as structured context —
  // the backend composes the turn, so no client-side prose here.
  const handleSendMessageWithCheck = useCallback((message: string) => {
    if ((lpGenerationQuota?.remainingQuota ?? 0) < 1) {
      openPaywall('not_enough_credits');
      return;
    }
    handleSendMessage(message, selectedBlock?.id);
  }, [lpGenerationQuota, openPaywall, handleSendMessage, selectedBlock?.id]);

  // Rewriting a published, in-sync page outdates it — inform the user it needs
  // republishing after the rewrite lands.
  const isInSync = !page.outdatedStatus || page.outdatedStatus === 'none';
  const { notify, modalProps } = usePublishedEditNotice(
    page.status === 'published' && isInSync,
  );

  const queryClient = useQueryClient();

  useEffect(() => {
    // The campaign query feeds the pages side panel (statuses, "Needs regeneration"
    // badges, "Generate all" count).
    const invalidateCampaign = () =>
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] });

    const unsubscribe = eventBus.subscribe((event) => {
      if (event.type === 'content_block_start') {
        const block = event.contentBlock;

        // One `data_update` per edited block — a page-wide ask emits several in
        // a single response, each retriggering the (deduplicated) invalidations.
        if (block.type === 'data_update') {
          void invalidateCampaign();
          // The rewritten variants live in the per-page query, not the campaign.
          // The helper leaves the chat-message query alone: refetching it
          // mid-stream would clobber the messages being streamed into local state.
          void invalidatePageQueries(queryClient, pageId);
          void queryClient.invalidateQueries({ queryKey: ['organization', workspaceId] });
          // The page content changed — inform the user it needs republishing.
          notify();
        }

        // "Apply to all pages": the backend emits `data_update` when it saves this
        // page, then learns the rule (an LLM call), then flags the sibling pages
        // for regeneration, then emits this block. The `data_update` refetch above
        // therefore lands before the flags exist — refetch again now that they do.
        if (block.type === 'context_summary' && block.subjectType === 'campaign_custom_instructions') {
          void invalidateCampaign();
        }
      }

      // Catch-all: whatever the agent did to the campaign's pages during this
      // turn, the panel reflects the settled state once the response ends.
      if (event.type === 'message_stop') {
        void invalidateCampaign();
      }
    });
    return () => unsubscribe();
  }, [eventBus, campaignId, pageId, workspaceId, queryClient, notify]);

  // The agent's "Campaign instructions" card: the editorial rules it just learned when the user
  // applied a change to all pages, with the regeneration notice.
  const customInstructionsPlugin = useMemo(
    () =>
      createContextSummaryPlugin({
        id: 'campaign-custom-instructions',
        emptyText: 'No new rule was recorded from this change.',
        // Collapsed by default: the rules are reference material, not something to read every time.
        defaultOpen: false,
      }),
    []
  );

  // The optimistic chip on the sent message: shown immediately from the local
  // selection, then replaced by the canonical context block the stream echoes
  // back (or stripped, if the selection was ignored and the turn ran globally).
  const composerContextBlock = useMemo<ContextContentBlock | null>(
    () =>
      selectedBlock
        ? {
            type: 'context',
            blockId: selectedBlock.id,
            blockVariantId: null,
            blockPath: null,
            label: selectedBlock.label ?? null,
          }
        : null,
    [selectedBlock],
  );

  // Composer chip: the selected block, removable without touching the canvas. Lower priority
  // than the suggestions input plugin (10): when the agent asks a question ("Apply this change
  // where?") its options must replace the composer, chip included.
  const inputPlugins = useMemo<AiChatInputPlugin[]>(() => {
    if (!selectedBlock) return [];
    return [
      createInputPlugin({
        id: 'selected-block',
        priority: 5,
        shouldActivate: () => true,
        render: () => (
          <div className="flex flex-col gap-2 px-4 pt-4">
            <div className="flex items-center gap-2">
              <span className="flex h-8 shrink-0 items-center gap-1 rounded-full border border-border bg-muted p-2">
                <MousePointer2 className="size-3" />
                <span className="max-w-40 truncate px-1 text-sm text-foreground">
                  {selectedBlock.label || 'Selected block'}
                </span>
                {onDeselectBlock && (
                  <button
                    type="button"
                    aria-label="Deselect block"
                    onClick={onDeselectBlock}
                    className="flex size-4 items-center justify-center rounded-full bg-input text-foreground"
                  >
                    <X className="size-3" />
                  </button>
                )}
              </span>
            </div>
          </div>
        ),
      }),
    ];
  }, [selectedBlock, onDeselectBlock]);

  return (
    <>
      <AiChat
        theme="compact"
        useDefaultPlugins={true}
        plugins={[customInstructionsPlugin]}
        eventBus={eventBus}
        messages={messages}
        onSendMessage={handleSendMessageWithCheck}
        onLoadMore={handleLoadMore}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        inputPlugins={inputPlugins}
        composerContextBlock={composerContextBlock}
        disabled={disabled}
        // The regeneration isn't streamed through this chat, so the indicator
        // is told about it: Tinct is "thinking" on this page until the job lands.
        indicatorStatus={disabled ? 'thinking' : undefined}
        placeholder={selectedBlock ? 'Ask Tinct to edit this block…' : 'Ask Tinct...'}
        emptyState={<PreviewChatEmpty />}
      />
      <ChangesNotLiveModal
        {...modalProps}
        step="some variant content"
        alertDescription="The edited variant pages need to be published."
        callToAction="Generate and publish the new version of your variant pages."
        actions={[
          { label: 'Ok noted !', variant: 'outline', onClick: () => modalProps.onOpenChange(false) },
        ]}
      />
    </>
  );
}

/**
 * The persistent conversation column on the left of the studio. Hosts the
 * Ask Tinct chat for the selected page variant — one conversation per page,
 * with the selected block travelling as structured context. Until a
 * generated page is selected it shows guidance instead.
 */
export const StudioChatPanel = ({
  campaignId,
  page,
  block,
  onDeselectBlock,
  disabled,
}: StudioChatPanelProps) => {
  return (
    // Width comes from the StudioLayout conversation column.
    <div className="flex h-full flex-col overflow-hidden">
      {page ? (
        <PageChat
          key={page.id}
          campaignId={campaignId}
          page={page}
          block={block}
          onDeselectBlock={onDeselectBlock}
          disabled={disabled}
        />
      ) : (
        <div className="flex h-full flex-col items-center justify-center gap-4 p-6">
          <PreviewChatEmpty />
          <p className="max-w-[240px] text-center text-sm text-muted-foreground">
            Pick a generated page in the panel to refine it with Tinct.
          </p>
        </div>
      )}
    </div>
  );
};
